/**
 * PROJ-005: RBAC Security System
 * Hook to get the access level for a specific privilege on an entity
 *
 * @module hooks/useAccessLevel
 *
 * NOTE: This file re-exports from usePermission.ts for backwards compatibility.
 * The main implementation is in usePermission.ts
 */

export {
  useAccessLevel,
  AccessLevel,
  type AccessLevelString,
  type Entity,
  type Privilege,
  accessLevelToString,
  stringToAccessLevel
} from './usePermission';

/**
 * Check if user has at least the specified access level for an entity/privilege
 *
 * @param entity - The entity to check
 * @param privilege - The privilege to check
 * @param minimumLevel - The minimum required access level
 * @returns boolean - true if user has at least the minimum level
 *
 * @example
 * ```tsx
 * const canManageBU = useHasAccessLevel('users', 'UPDATE', AccessLevel.BUSINESS_UNIT);
 *
 * if (canManageBU) {
 *   // User can update users in their business unit
 * }
 * ```
 */
import { useAccessLevel, AccessLevel, Entity, Privilege } from './usePermission';
import { stringToAccessLevel } from './usePermission';

export function useHasAccessLevel(
  entity: Entity,
  privilege: Privilege,
  minimumLevel: AccessLevel
): boolean {
  const currentLevelString = useAccessLevel(entity, privilege);
  const currentLevel = stringToAccessLevel(currentLevelString);
  return currentLevel >= minimumLevel;
}
